// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Chat settings > Chats panel. Which connected sources share the panel
// with Konvo chats, and how long a chat may sit idle before it folds into
// Quiet. Stored per device, see ChatsPanel state.

import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import ChatsPanel, {QUIET_AFTER_DAYS_OPTIONS} from '@app/features/channel/state/ChatsPanel';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {SwitchGroup, SwitchGroupItem} from '@app/features/ui/components/SwitchGroup';
import {ClockIcon, ListIcon} from '@app/features/ui/icons/Icons';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {CompactComboboxRow} from '@app/features/user/components/modals/tabs/components/CompactComboboxRow';
import type {I18n} from '@lingui/core';
import {msg, plural} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const SHOW_MAIL_DESCRIPTOR = msg({
	message: 'Show mail in the Chats panel',
	comment:
		'Toggle in Chat settings > Chats panel. When on, unread Outlook mail appears as one digest row in the Chats list.',
});
const SHOW_WHATSAPP_DESCRIPTOR = msg({
	message: 'Show WhatsApp chats in the Chats panel',
	comment: 'Toggle in Chat settings > Chats panel. When on, linked WhatsApp chats appear as rows in the Chats list.',
});
const SHOW_TEAMS_DESCRIPTOR = msg({
	message: 'Show Teams chats in the Chats panel',
	comment:
		'Toggle in Chat settings > Chats panel. When off, conversations linked to Microsoft Teams are hidden from the Chats list.',
});
const QUIET_AFTER_DESCRIPTOR = msg({
	message: 'Fold quiet chats after',
	comment: 'Accessible label of the radio group choosing how many idle days move a chat into the Quiet section.',
});
const NEVER_DESCRIPTOR = msg({
	message: 'Never',
	comment: 'Radio option: chats never fold into the Quiet section.',
});
const NEVER_DESC_DESCRIPTOR = msg({
	message: 'Keep every chat in Recent.',
	comment: 'Description under the Never option for folding quiet chats.',
});

const quietOptions = (i18n: I18n): ReadonlyArray<RadioOption<number>> =>
	QUIET_AFTER_DAYS_OPTIONS.map((days) =>
		days === 0
			? {value: 0, name: i18n._(NEVER_DESCRIPTOR), desc: i18n._(NEVER_DESC_DESCRIPTOR)}
			: {
					value: days,
					name: plural(days, {one: '# day', other: '# days'}),
				},
	);

export const ChatsPanelTabContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	return (
		<>
			{!MobileLayout.isMobileLayout() && (
				<SettingsTabSection
					title={<Trans>Desktop chat list</Trans>}
					description={
						<Trans>
							One line per chat with a small avatar, the name, a short preview and the time. Saved on this device;
							mobile stays unchanged.
						</Trans>
					}
					data-flx="user.chat-settings-tab.chats-panel-tab.desktop-list"
				>
					<SwitchGroup>
						<SwitchGroupItem
							label={<Trans>Compact chat list</Trans>}
							value={ChatsPanel.compactDesktop}
							onChange={(value) => ChatsPanel.setCompactDesktop(value)}
							icon={<ListIcon size={20} />}
						/>
					</SwitchGroup>
				</SettingsTabSection>
			)}
			<SettingsTabSection
				title={<Trans>Connected sources</Trans>}
				description={
					<Trans>Everything can land in one list. Turn a source off to keep it out of the Chats panel.</Trans>
				}
				data-flx="user.chat-settings-tab.chats-panel-tab.sources"
			>
				<SwitchGroup data-flx="user.chat-settings-tab.chats-panel-tab.switch-group">
					<SwitchGroupItem
						label={i18n._(SHOW_MAIL_DESCRIPTOR)}
						value={ChatsPanel.showMail}
						onChange={(value) => ChatsPanel.setShowMail(value)}
						icon={<OutlookLogo size={20} />}
						data-flx="user.chat-settings-tab.chats-panel-tab.show-mail"
					/>
					<SwitchGroupItem
						label={i18n._(SHOW_WHATSAPP_DESCRIPTOR)}
						value={ChatsPanel.showWhatsApp}
						onChange={(value) => ChatsPanel.setShowWhatsApp(value)}
						icon={<WhatsAppLogo size={20} />}
						data-flx="user.chat-settings-tab.chats-panel-tab.show-whatsapp"
					/>
					<SwitchGroupItem
						label={i18n._(SHOW_TEAMS_DESCRIPTOR)}
						value={ChatsPanel.showTeams}
						onChange={(value) => ChatsPanel.setShowTeams(value)}
						icon={<TeamsLogo size={20} />}
						data-flx="user.chat-settings-tab.chats-panel-tab.show-teams"
					/>
				</SwitchGroup>
			</SettingsTabSection>
			{MobileLayout.enabled ? (
				<SettingsTabSection
					title={<Trans>Fold quiet chats after</Trans>}
					description={
						<Trans>
							A chat stays in Recent while it is unread or has had a message within this window. Older chats move to the
							collapsed Quiet section.
						</Trans>
					}
					data-flx="user.chat-settings-tab.chats-panel-tab.quiet"
				>
					<RadioGroup
						options={quietOptions(i18n)}
						value={ChatsPanel.quietAfterDays}
						onChange={(value) => ChatsPanel.setQuietAfterDays(value)}
						aria-label={i18n._(QUIET_AFTER_DESCRIPTOR)}
						data-flx="user.chat-settings-tab.chats-panel-tab.quiet-radio"
					/>
				</SettingsTabSection>
			) : (
				<SettingsTabSection data-flx="user.chat-settings-tab.chats-panel-tab.quiet">
					<CompactComboboxRow<number>
						icon={<ClockIcon size={20} />}
						label={i18n._(QUIET_AFTER_DESCRIPTOR)}
						description={
							<Trans>
								A chat stays in Recent while it is unread or has had a message within this window. Older chats move to
								the collapsed Quiet section.
							</Trans>
						}
						value={ChatsPanel.quietAfterDays}
						options={quietOptions(i18n).map((option) => ({value: option.value, label: String(option.name)}))}
						onChange={(value) => ChatsPanel.setQuietAfterDays(value)}
						isSearchable={false}
						controlWidth="small"
						dataFlx="user.chat-settings-tab.chats-panel-tab.quiet-radio"
					/>
				</SettingsTabSection>
			)}
		</>
	);
});
